import { MinuteStepSizeDto } from '../../../../features/Admin/CompanySettings/types';
import { Card } from '../../../Cards';
import styles from '../style.m.less';
import Selector from './selector';
import {
    HOURS_12,
    HOURS_24,
    isPm,
    MINUTES,
    MINUTES_10,
    MINUTES_15,
    MINUTES_5,
    MINUTES_ZERO,
    toHoursMinutes,
    toOption,
} from './time';

export type AMPM = 'AM' | 'PM';
export type HourFormat = '24hr' | '12hr';

const AmPmOptions: AMPM[] = ['AM', 'PM'];

interface Props {
    timeZone: string;
    format: HourFormat;
    value?: string;
    onChange: (value: string) => void;
    stepSizeMins: MinuteStepSizeDto | undefined;
}

const getHours = (format: HourFormat) => {
    switch (format) {
        case '12hr':
            return HOURS_12;
        case '24hr':
            return HOURS_24;
    }
};

const getMinutes = (stepSize: Props['stepSizeMins']) => {
    switch (stepSize) {
        case 0:
            return MINUTES_ZERO;
        case 1:
            return MINUTES;
        case 5:
            return MINUTES_5;
        case 10:
            return MINUTES_10;
        case 15:
            return MINUTES_15;
    }
};

const getSelectedHour = (hour: number | null, format: HourFormat) => {
    if (hour === null) {
        return null;
    } else if (format === '12hr') {
        return hour % 12;
    } else {
        return hour % 24;
    }
};

export default function TimePickerPopup({ value, timeZone, format, onChange, stepSizeMins }: Props) {
    const [hour24, minute] = toHoursMinutes(value);
    const hourOptions = getHours(format);
    const hour = getSelectedHour(hour24, format);
    const minuteOptions = getMinutes(stepSizeMins);
    const isHourPm = format === '12hr' && hour24 !== null && isPm(hour24);

    const getHour24 = (newHour: string): number => {
        const convertToPm = isHourPm && typeof parseInt(newHour) !== 'undefined';
        if (convertToPm) {
            return Math.max(12, (parseInt(newHour) + 12) % 24);
        } else if (format === '12hr') {
            return parseInt(newHour) % 12;
        }
        return parseInt(newHour) % 24;
    };

    const handleChange = (parts: number[]) => {
        onChange(parts.map(toOption).join(':'));
    };

    const handleAmPmChange = (newOption: AMPM) => {
        if (newOption === 'AM' || hour === 12) {
            handleChange([hour || 0, minute || 0]);
        } else {
            handleChange([hour !== null ? hour + 12 : 0, minute || 0]);
        }
    };

    return (
        <>
            <div className={styles.flexWrapper}>
                <Selector
                    options={hourOptions}
                    selectedIndex={hour}
                    onSelect={newHour => handleChange([getHour24(newHour), minute || 0])}
                />
                <Selector
                    options={minuteOptions}
                    selectedIndex={minute !== null ? minuteOptions?.indexOf(toOption(minute)) : null}
                    onSelect={newMinute => handleChange([hour24 || 0, parseInt(newMinute)])}
                />
                {format === '12hr' && (
                    <Selector
                        options={AmPmOptions}
                        selectedIndex={isHourPm ? 1 : 0}
                        onSelect={handleAmPmChange}
                        scrollToSelected={false}
                    />
                )}
            </div>
            <Card className={styles.infoBox}>All times are {timeZone} time</Card>
        </>
    );
}
